import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';

/**
 * Super-admin only.
 *
 * SMS is configured per Dealer Group (dealer/tenant) + Region (e.g. AU/US).
 * Outbound site_id is OPTIONAL (attribution only) via DealerSmsConfig.primary_site_id.
 */
export default function CommsSetupWizard() {
  const [dealers, setDealers] = useState([]);
  const [selectedDealerId, setSelectedDealerId] = useState('');
  const [region, setRegion] = useState('AU');

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const [toNumberE164, setToNumberE164] = useState('');
  const [messagingServiceSid, setMessagingServiceSid] = useState('');
  const [subaccountSid, setSubaccountSid] = useState('');
  const [primarySiteId, setPrimarySiteId] = useState('');
  const [active, setActive] = useState(true);

  const [testToNumber, setTestToNumber] = useState('');
  const [testMessage, setTestMessage] = useState('Test SMS from Tucana');

  const defaultApiBase = useMemo(() => {
    const configured = process.env.REACT_APP_PUBLIC_API_URL || process.env.REACT_APP_API_URL;
    if (configured && configured.trim()) return configured.replace(/\/$/, '');

    const origin = window.location.origin;
    try {
      const u = new URL(origin);
      if (u.hostname.startsWith('crm.')) {
        u.hostname = `api.${u.hostname.slice(4)}`;
        return u.toString().replace(/\/$/, '');
      }
    } catch (e) {
      // ignore
    }
    return origin.replace(/\/$/, '');
  }, []);

  const [publicApiBase, setPublicApiBase] = useState(defaultApiBase);

  const twilioInboundUrl = `${publicApiBase}/api/webhooks/twilio/inbound`;
  const twilioStatusUrl = `${publicApiBase}/api/webhooks/twilio/status`;
  const mailgunInboundUrl = `${publicApiBase}/api/webhooks/email/mailgun`;

  useEffect(() => {
    loadDealers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedDealerId) return;
    loadSmsConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDealerId, region]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage({ type: 'success', text: 'Copied to clipboard' });
      setTimeout(() => setMessage(null), 1500);
    } catch (error) {
      console.error('Copy failed:', error);
      setMessage({ type: 'error', text: 'Copy failed' });
    }
  };

  const loadDealers = async () => {
    setLoading(true);
    try {
      const data = await api.listDealers();
      setDealers(data || []);
      if ((data || []).length && !selectedDealerId) {
        setSelectedDealerId(data[0].id);
      }
    } catch (error) {
      console.error('Failed to load dealers:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to load dealers' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setToNumberE164('');
    setMessagingServiceSid('');
    setSubaccountSid('');
    setPrimarySiteId('');
    setActive(true);
  };

  const loadSmsConfig = async () => {
    if (!selectedDealerId) return;
    setBusy(true);
    setMessage(null);

    try {
      const rows = await api.getDealerSmsConfigs(selectedDealerId);
      const r = (region || 'AU').trim().toUpperCase();
      const row = (rows || []).find(x => (x.region || '').toUpperCase() === r);

      if (!row) {
        resetForm();
        setMessage({ type: 'info', text: `No SMS config found for ${r}. Create one below.` });
        return;
      }

      setToNumberE164(row.to_number_e164 || '');
      setMessagingServiceSid(row.twilio_messaging_service_sid || '');
      setSubaccountSid(row.twilio_subaccount_sid || '');
      setPrimarySiteId(row.primary_site_id || '');
      setActive(!!row.active);
    } catch (error) {
      console.error('Failed to load SMS config:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to load SMS config' });
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!selectedDealerId) return;
    const r = (region || 'AU').trim().toUpperCase();

    if (!toNumberE164.trim()) {
      setMessage({ type: 'error', text: 'shared to_number_e164 is required' });
      return;
    }

    setBusy(true);
    setMessage(null);

    try {
      await api.upsertDealerSmsConfig(selectedDealerId, {
        region: r,
        to_number: toNumberE164.trim(),
        twilio_messaging_service_sid: messagingServiceSid.trim() || null,
        twilio_subaccount_sid: subaccountSid.trim() || null,
        primary_site_id: primarySiteId.trim() || null,
        active: !!active,
      });

      setMessage({ type: 'success', text: `Saved SMS config for ${r}` });
      await loadSmsConfig();
    } catch (error) {
      console.error('Failed to save SMS config:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save SMS config' });
    } finally {
      setBusy(false);
    }
  };

  const handleSendTestSms = async () => {
    if (!selectedDealerId) return;
    const r = (region || 'AU').trim().toUpperCase();

    if (!testToNumber.trim()) {
      setMessage({ type: 'error', text: 'Enter a test destination number' });
      return;
    }

    setBusy(true);
    setMessage(null);

    try {
      const res = await api.sendDealerTestSms(selectedDealerId, {
        region: r,
        to_number: testToNumber.trim(),
        message: testMessage.trim() || 'Test SMS from Tucana',
      });

      setMessage({ type: 'success', text: `Test SMS sent. Provider id: ${res.provider_message_id || '(unknown)'}` });
    } catch (error) {
      console.error('Failed to send test SMS:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to send test SMS' });
    } finally {
      setBusy(false);
    }
  };

  const selectedDealer = dealers.find(d => d.id === selectedDealerId);

  if (loading) {
    return <div style={styles.loading}>Loading comms setup…</div>;
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>📡 Comms Setup (Super Admin)</h1>
      <p style={styles.subtitle}>
        Configure SMS at the <b>Dealer Group + Region</b> level (shared number). Site selection is optional and used only for attribution.
      </p>

      {message && (
        <div
          style={{
            ...styles.message,
            backgroundColor:
              message.type === 'success'
                ? 'rgba(34, 197, 94, 0.2)'
                : message.type === 'info'
                  ? 'rgba(59, 130, 246, 0.18)'
                  : 'rgba(239, 68, 68, 0.2)',
            color:
              message.type === 'success'
                ? '#86efac'
                : message.type === 'info'
                  ? '#bfdbfe'
                  : '#fca5a5',
          }}
        >
          {message.text}
        </div>
      )}

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>Selector</h2>
          <p style={styles.cardSubtitle}>Pick a dealer group and region.</p>
        </div>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Dealer Group</label>
            <select
              value={selectedDealerId}
              onChange={(e) => setSelectedDealerId(e.target.value)}
              style={styles.select}
              disabled={busy}
            >
              {dealers.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name || d.id}
                </option>
              ))}
            </select>
            <div style={styles.helpText}>
              Selected: <code style={styles.codeInline}>{selectedDealer?.id || ''}</code>
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Region</label>
            <input
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              style={styles.input}
              placeholder="AU"
              disabled={busy}
            />
            <div style={styles.helpText}>Typically <code style={styles.codeInline}>AU</code> or <code style={styles.codeInline}>US</code>.</div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Public API Base URL</label>
            <input
              value={publicApiBase}
              onChange={(e) => setPublicApiBase(e.target.value)}
              style={styles.input}
              placeholder="https://api.yourdomain.com"
              disabled={busy}
            />
            <div style={styles.helpText}>Used to generate webhook URLs to paste into Twilio/Mailgun.</div>
          </div>
        </div>
      </div>

      <div style={styles.grid}>
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Webhooks</h2>
            <p style={styles.cardSubtitle}>Paste these into Twilio/Mailgun dashboards.</p>
          </div>

          <div style={styles.section}>
            <div style={styles.kvRow}>
              <div>
                <div style={styles.kvLabel}>Twilio inbound SMS webhook</div>
                <div style={styles.kvValue}><code style={styles.code}>{twilioInboundUrl}</code></div>
              </div>
              <button style={styles.secondaryButton} onClick={() => copyToClipboard(twilioInboundUrl)}>Copy</button>
            </div>

            <div style={styles.kvRow}>
              <div>
                <div style={styles.kvLabel}>Twilio status callback webhook</div>
                <div style={styles.kvValue}><code style={styles.code}>{twilioStatusUrl}</code></div>
              </div>
              <button style={styles.secondaryButton} onClick={() => copyToClipboard(twilioStatusUrl)}>Copy</button>
            </div>

            <div style={styles.kvRow}>
              <div>
                <div style={styles.kvLabel}>Mailgun inbound email webhook</div>
                <div style={styles.kvValue}><code style={styles.code}>{mailgunInboundUrl}</code></div>
              </div>
              <button style={styles.secondaryButton} onClick={() => copyToClipboard(mailgunInboundUrl)}>Copy</button>
            </div>

            <div style={styles.helpText}>
              Inbound SMS is routed by matching the Twilio <b>To</b> number against the configured <code style={styles.codeInline}>to_number_e164</code> for the dealer + region.
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>SMS Config</h2>
            <p style={styles.cardSubtitle}>Shared number + Twilio sender configuration.</p>
          </div>

          <div style={styles.section}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Shared to_number_e164 (required)</label>
              <input
                value={toNumberE164}
                onChange={(e) => setToNumberE164(e.target.value)}
                style={styles.input}
                placeholder="+614xxxxxxxx"
                disabled={busy}
              />
              <div style={styles.helpText}>This is the shared group number Twilio receives messages on (per dealer + region).</div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Twilio Messaging Service SID (optional)</label>
              <input
                value={messagingServiceSid}
                onChange={(e) => setMessagingServiceSid(e.target.value)}
                style={styles.input}
                placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                disabled={busy}
              />
              <div style={styles.helpText}>If set, outbound SMS uses the Messaging Service. If blank, uses the shared number as From.</div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Twilio Subaccount SID (optional)</label>
              <input
                value={subaccountSid}
                onChange={(e) => setSubaccountSid(e.target.value)}
                style={styles.input}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                disabled={busy}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Primary site_id (optional)</label>
              <input
                value={primarySiteId}
                onChange={(e) => setPrimarySiteId(e.target.value)}
                style={styles.input}
                placeholder="(site UUID)"
                disabled={busy}
              />
              <div style={styles.helpText}>Only used for attribution on outbound messages (optional).</div>
            </div>

            <label style={styles.toggle}>
              <input
                type="checkbox"
                checked={!!active}
                onChange={(e) => setActive(e.target.checked)}
                disabled={busy}
              />
              <span style={styles.toggleLabel}>{active ? 'Active' : 'Inactive'}</span>
            </label>

            <div style={styles.actionsRow}>
              <button style={styles.primaryButton} onClick={handleSave} disabled={busy}>
                {busy ? 'Saving…' : 'Save SMS Config'}
              </button>
              <button style={styles.secondaryButton} onClick={loadSmsConfig} disabled={busy}>
                Reload
              </button>
            </div>

            <div style={styles.divider} />

            <h3 style={styles.subsectionTitle}>Send test SMS</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>To number</label>
              <input
                value={testToNumber}
                onChange={(e) => setTestToNumber(e.target.value)}
                style={styles.input}
                placeholder="+614xxxxxxxx"
                disabled={busy}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Message</label>
              <input
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                style={styles.input}
                placeholder="Test SMS from Tucana"
                disabled={busy}
              />
            </div>
            <button style={styles.primaryButton} onClick={handleSendTestSms} disabled={busy}>
              {busy ? 'Sending…' : 'Send test SMS'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    padding: '24px',
  },
  loading: {
    padding: '24px',
    color: '#94a3b8',
  },
  title: {
    margin: 0,
    fontSize: '28px',
  },
  subtitle: {
    marginTop: '8px',
    color: '#94a3b8',
    maxWidth: '900px',
  },
  message: {
    padding: '12px 16px',
    borderRadius: '10px',
    marginTop: '16px',
    marginBottom: '16px',
    border: '1px solid rgba(148, 163, 184, 0.25)',
  },
  card: {
    background: '#1e293b',
    borderRadius: '16px',
    padding: '20px',
    border: '1px solid #334155',
    boxShadow: '0 10px 24px rgba(0,0,0,0.18)',
    marginTop: '16px',
  },
  cardHeader: {
    marginBottom: '14px',
  },
  cardTitle: {
    margin: 0,
    fontSize: '18px',
  },
  cardSubtitle: {
    margin: '6px 0 0',
    fontSize: '13px',
    color: '#94a3b8',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '16px',
    marginTop: '16px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '12px',
    alignItems: 'start',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '12px',
    color: '#cbd5e1',
  },
  input: {
    background: '#0f172a',
    color: '#f8fafc',
    border: '1px solid #334155',
    borderRadius: '10px',
    padding: '10px 12px',
  },
  select: {
    background: '#0f172a',
    color: '#f8fafc',
    border: '1px solid #334155',
    borderRadius: '10px',
    padding: '10px 12px',
  },
  helpText: {
    fontSize: '12px',
    color: '#94a3b8',
    lineHeight: 1.3,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  kvRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    alignItems: 'center',
  },
  kvLabel: {
    fontSize: '12px',
    color: '#cbd5e1',
    marginBottom: '4px',
  },
  kvValue: {
    color: '#e2e8f0',
    fontSize: '12px',
    overflowWrap: 'anywhere',
  },
  code: {
    fontSize: '12px',
  },
  codeInline: {
    background: '#0b1220',
    border: '1px solid #1f2a44',
    borderRadius: '8px',
    padding: '2px 6px',
  },
  divider: {
    height: '1px',
    background: '#334155',
    margin: '4px 0',
  },
  actionsRow: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  primaryButton: {
    background: '#38bdf8',
    color: '#0f172a',
    padding: '10px 14px',
    borderRadius: '10px',
    border: 'none',
    fontWeight: 700,
    cursor: 'pointer',
  },
  secondaryButton: {
    background: '#0f172a',
    color: '#e2e8f0',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1px solid #334155',
    fontWeight: 600,
    cursor: 'pointer',
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#0f172a',
    padding: '8px 12px',
    borderRadius: '999px',
    border: '1px solid #334155',
    width: 'fit-content',
  },
  toggleLabel: {
    fontSize: '14px',
  },
  subsectionTitle: {
    margin: '0 0 4px',
    fontSize: '14px',
  },
};
